import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import { DealService } from 'services/crm'

const dealsAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.updatedAt < b.updatedAt ? 1 : -1),
})

export const fetchDeals = createAsyncThunk(
  'crm/deals/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await DealService.getAll(params)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching deals')
    }
  },
)

export const fetchDeal = createAsyncThunk(
  'crm/deals/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await DealService.get(id)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching deal')
    }
  },
)

export const createDeal = createAsyncThunk(
  'crm/deals/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await DealService.create(data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating deal')
    }
  },
)

export const updateDeal = createAsyncThunk(
  'crm/deals/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await DealService.update(id, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating deal')
    }
  },
)

export const removeDeal = createAsyncThunk(
  'crm/deals/remove',
  async (id, { rejectWithValue }) => {
    try {
      await DealService.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting deal')
    }
  },
)

/**
 * Move a deal to a new stage. Handles optimistic UI:
 *  1. Store is updated synchronously via the `optimisticMove` reducer
 *     dispatched just before the thunk from the UI layer.
 *  2. Thunk awaits server response. On success we re-upsert with the
 *     canonical shape (may include derived fields like `status`, `wonAt`).
 *  3. On failure, the UI layer should dispatch `rollbackMove` with the
 *     previous stageId.
 */
export const moveDealStage = createAsyncThunk(
  'crm/deals/move',
  async ({ dealId, stageId, updateProbability, lostReason }, { rejectWithValue }) => {
    try {
      const res = await DealService.move(dealId, { stageId, updateProbability, lostReason })
      return res.data
    } catch (e) {
      return rejectWithValue({
        dealId,
        message: e.response?.data?.message || 'Error moving deal',
      })
    }
  },
)

const initialState = dealsAdapter.getInitialState({
  loading: 'idle',
  error: null,
})

const dealsSlice = createSlice({
  name: 'crmDeals',
  initialState,
  reducers: {
    optimisticMove: (state, action) => {
      const { dealId, stageId } = action.payload
      const deal = state.entities[dealId]
      if (deal) deal.stageId = stageId
    },
    rollbackMove: (state, action) => {
      const { dealId, previousStageId } = action.payload
      const deal = state.entities[dealId]
      if (deal) deal.stageId = previousStageId
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeals.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.loading = 'idle'
        dealsAdapter.setAll(state, action.payload)
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.loading = 'idle'
        state.error = action.payload
      })
      .addCase(fetchDeal.fulfilled, (state, action) => {
        dealsAdapter.upsertOne(state, action.payload)
      })
      .addCase(createDeal.fulfilled, (state, action) => {
        dealsAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateDeal.fulfilled, (state, action) => {
        dealsAdapter.upsertOne(state, action.payload)
      })
      .addCase(removeDeal.fulfilled, (state, action) => {
        dealsAdapter.removeOne(state, action.payload)
      })
      .addCase(moveDealStage.fulfilled, (state, action) => {
        dealsAdapter.upsertOne(state, action.payload)
      })
  },
})

export const { optimisticMove, rollbackMove } = dealsSlice.actions

const selectors = dealsAdapter.getSelectors((state) => state.crmDeals)
export const selectAllDeals = selectors.selectAll
export const selectDealById = selectors.selectById

export const selectDealsByPipeline = (pipelineId) =>
  createSelector([selectAllDeals], (deals) => deals.filter((d) => d.pipelineId === pipelineId))

export const selectDealsByStage = (stageId) =>
  createSelector([selectAllDeals], (deals) =>
    deals.filter((d) => d.stageId === stageId).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
  )

export const selectDealsKpis = createSelector([selectAllDeals], (deals) => {
  const open = deals.filter((d) => d.status === 'open')
  const won = deals.filter((d) => d.status === 'won')
  const lost = deals.filter((d) => d.status === 'lost')
  const pipelineValueUsd = open.reduce((sum, d) => sum + (d.currency === 'USD' ? d.value || 0 : 0), 0)
  const wonValueUsd = won.reduce((sum, d) => sum + (d.currency === 'USD' ? d.value || 0 : 0), 0)
  const decided = won.length + lost.length
  const winRate = decided ? Math.round((won.length / decided) * 100) : null
  return {
    totalOpen: open.length,
    totalWon: won.length,
    totalLost: lost.length,
    pipelineValueUsd,
    wonValueUsd,
    winRate,
  }
})

export default dealsSlice.reducer
